import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { pushEvent } from "@/lib/gtm";
import { MONO } from "@/components/home-a/theme";
import { Reveal } from "@/components/home-a/Reveal";
import { useTr, Section, CtaBand } from "@/components/site/Kit";
import { DemoReportDialog } from "./DemoReportDialog";
import { DemoVignette } from "./Vignettes";
import type { DemoKind } from "./demoKinds";
import { StartFreeLink, useStartFree } from "@/components/site/StartFree";

/**
 * The calls to action of the inner marketing pages (solutions and features).
 *
 * These pages are read by people who already know they want the tool, so they
 * lead to an account (primary: a free signup, or the free trial once it
 * is released, see site/StartFree) and to a sample report on a demo page
 * (secondary), not to the free audit, which stays on the home page, the
 * guides, the comparisons and the blog. The sample opens in a dialog with the
 * real report component.
 */

/**
 * Opens the sample report for `kind`. Fires demo_open with where it was opened from.
 * White by default, the second action next to the green one; "outline" for the
 * quiet text-like uses.
 */
export function DemoButton({ kind, location, className, children, variant = "white" }: { kind: DemoKind; location: string; className?: string; children?: ReactNode; variant?: "white" | "outline" }) {
  const { tr } = useTr();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-track="demo_open"
        onClick={() => {
          setOpen(true);
          pushEvent("demo_open", { analysis_type: kind, cta_location: location });
        }}
        className={cn(
          "group inline-flex h-12 items-center justify-center gap-2.5 rounded-[10px] pl-2.5 pr-5 text-[15px]",
          variant === "white"
            ? "ec-cta-white font-semibold"
            : "border border-[hsl(var(--ec-line-strong))] bg-foreground/[0.03] font-medium text-foreground transition-colors hover:border-[hsl(var(--ec-mint)/0.55)] hover:bg-primary/[0.06]",
          className,
        )}
      >
        <span aria-hidden className={cn("grid h-7 w-7 place-items-center rounded-full transition-transform group-hover:scale-110", variant === "white" ? "bg-[hsl(157_62%_30%/0.12)] text-[hsl(157_62%_26%)]" : "bg-primary/15 text-[hsl(var(--ec-mint))]")}>
          <svg width="10" height="12" viewBox="0 0 10 12" fill="currentColor"><path d="M0 0l10 6-10 6z" /></svg>
        </span>
        {children ?? tr("Δείτε δείγμα report", "See a sample report")}
      </button>
      <DemoReportDialog kind={kind} open={open} onOpenChange={setOpen} />
    </>
  );
}

/** The plans, as the quieter action beside the signup. */
function PlansLink({ className, children }: { className?: string; children?: ReactNode }) {
  const { tr } = useTr();
  return (
    <Link to="/pricing" className={cn("ec-cta-white inline-flex h-12 items-center justify-center gap-2 rounded-[10px] px-5 text-[15px] font-semibold", className)}>
      {children ?? tr("Δείτε τα πλάνα", "See the plans")}
    </Link>
  );
}

/** Hero actions: the free account first, the sample second, one line of facts under them. */
export function InnerHeroCtas({ kind, className, center = false }: { kind: DemoKind; className?: string; center?: boolean }) {
  const start = useStartFree();
  return (
    <div data-section="inner_hero" className={cn(center && "text-center", className)}>
      <div className={cn("flex flex-col gap-3 sm:flex-row sm:items-center", center && "sm:justify-center")}>
        <StartFreeLink location="inner_hero" />
        <DemoButton kind={kind} location="inner_hero" />
      </div>
      <p className="mt-3.5 text-[13px] text-[hsl(var(--ec-muted))]">{start.facts.join("  ·  ")}</p>
    </div>
  );
}

/**
 * Mid-page break for long pages: a live preview of the sample on one side,
 * one claim and the two actions on the other. The preview itself opens the
 * sample.
 */
export function InlineDemo({ kind, kicker, title, body, flip = false }: { kind: DemoKind; kicker?: string; title: string; body: ReactNode; flip?: boolean }) {
  const { tr } = useTr();
  const [open, setOpen] = useState(false);
  return (
    <Section className="py-10 sm:py-14">
      <Reveal>
        <div data-section="inner_mid" className="relative overflow-hidden rounded-[26px] border border-border bg-card">
          <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[radial-gradient(circle,hsl(var(--primary)/0.14),transparent_70%)]" />
          <div className={cn("relative grid grid-cols-1 items-center gap-8 p-6 sm:p-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-12", flip && "lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]")}>
            <button
              type="button"
              data-track="demo_open"
              aria-label={tr("Άνοιγμα του δείγματος report", "Open the sample report")}
              onClick={() => {
                setOpen(true);
                pushEvent("demo_open", { analysis_type: kind, cta_location: "inner_mid_preview" });
              }}
              className={cn("group relative block w-full cursor-zoom-in pb-6 pt-7 text-left transition-transform duration-300 hover:-translate-y-0.5", flip && "lg:order-2")}
            >
              <DemoVignette kind={kind} tr={tr} />
              <span className="absolute bottom-0 right-2 inline-flex items-center gap-1.5 rounded-full bg-background/90 px-2.5 py-1 text-[11px] text-[hsl(var(--ec-text-2))] opacity-80 transition-opacity group-hover:opacity-100" style={{ fontFamily: MONO }}>
                {tr("Demo σελίδα · πατήστε για το πλήρες report", "Demo page · click for the full report")}
              </span>
            </button>
            <div className={cn(flip && "lg:order-1")}>
              {kicker && (
                <p className="inline-flex items-center gap-2.5 text-[12px] uppercase tracking-[0.12em] text-[hsl(var(--ec-mint))]" style={{ fontFamily: MONO }}>
                  <span aria-hidden className="h-px w-[18px] bg-[hsl(var(--ec-mint))]" />
                  {kicker}
                </p>
              )}
              <h2 className="mt-3 text-[28px] font-semibold leading-[1.1] tracking-[-0.03em] sm:text-[34px]">{title}</h2>
              <div className="mt-4 text-[16px] leading-relaxed text-[hsl(var(--ec-text-2))]">{body}</div>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <StartFreeLink location="inner_mid" />
                <DemoButton kind={kind} location="inner_mid">{tr("Ανοίξτε όλο το δείγμα", "Open the full sample")}</DemoButton>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
      <DemoReportDialog kind={kind} open={open} onOpenChange={setOpen} />
    </Section>
  );
}

/**
 * The animated preview of a sample on its own: clicking it opens the full
 * sample report. For pages that show several samples side by side.
 */
export function DemoPreview({ kind, location, className, caption = true }: { kind: DemoKind; location: string; className?: string; caption?: boolean }) {
  const { tr } = useTr();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-track="demo_open"
        aria-label={tr("Άνοιγμα του δείγματος report", "Open the sample report")}
        onClick={() => {
          setOpen(true);
          pushEvent("demo_open", { analysis_type: kind, cta_location: location });
        }}
        className={cn("group relative block w-full cursor-zoom-in pb-6 pt-7 text-left transition-transform duration-300 hover:-translate-y-0.5", className)}
      >
        <DemoVignette kind={kind} tr={tr} />
        {caption && (
          <span className="absolute bottom-0 right-2 inline-flex items-center gap-1.5 rounded-full bg-background/90 px-2.5 py-1 text-[11px] text-[hsl(var(--ec-text-2))] opacity-80 transition-opacity group-hover:opacity-100" style={{ fontFamily: MONO }}>
            {tr("Demo σελίδα · πατήστε για το πλήρες report", "Demo page · click for the full report")}
          </span>
        )}
      </button>
      <DemoReportDialog kind={kind} open={open} onOpenChange={setOpen} />
    </>
  );
}

/** A slim strip between sections: one line and the two actions. */
export function InlineCta({ kind, title, body }: { kind: DemoKind; title: string; body?: string }) {
  return (
    <Section className="py-6 sm:py-8">
      <Reveal>
        <div data-section="inner_strip" className="flex flex-col gap-5 rounded-[18px] border border-[hsl(var(--ec-mint)/0.25)] bg-primary/[0.05] px-6 py-6 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-[640px]">
            <p className="text-[19px] font-semibold leading-snug tracking-[-0.02em] text-foreground">{title}</p>
            {body && <p className="mt-1.5 text-[15px] leading-relaxed text-[hsl(var(--ec-text-2))]">{body}</p>}
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
            <StartFreeLink location="inner_strip" />
            <DemoButton kind={kind} location="inner_strip" />
          </div>
        </div>
      </Reveal>
    </Section>
  );
}

/** The closing band of an inner page: the free account, the plans, the sample. */
export function InnerCtaBand({ kind, title, body }: { kind: DemoKind; title?: string; body?: string }) {
  const { tr } = useTr();
  const start = useStartFree();
  return (
    <div data-section="inner_band">
      <CtaBand
        title={title ?? tr("Δείτε τι χάνουν οι σελίδες σας", "See what your pages are losing")}
        body={body ?? `${start.pitch} ${tr("Ή δείτε πρώτα πώς μοιάζει ένα report.", "Or see what a report looks like first.")}`}
        form={false}
      >
        <div className="mt-8 flex w-full max-w-[560px] flex-col gap-3 sm:flex-row sm:flex-wrap">
          <StartFreeLink location="inner_band" />
          <PlansLink />
        </div>
        <DemoButton kind={kind} location="inner_band" variant="outline" className="mt-3 h-10 border-0 bg-transparent pl-0 text-[14px] text-[hsl(var(--ec-text-2))] hover:bg-transparent hover:text-foreground">
          {tr("ή δείτε πρώτα ένα δείγμα report", "or see a sample report first")}
        </DemoButton>
      </CtaBand>
    </div>
  );
}
